import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, LogOut, Loader2, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * ActivationGate — wraps the authenticated app AFTER LegalGate. If the
 * user's `activation_status` is "pending_card", the app is walled off and
 * a full-screen prompt asks them to complete Stripe Checkout to attach a
 * payment method (7-day trial, card required, auto-converts to paid).
 *
 * Bypass routes: /billing (so post-checkout success page can render + the
 * reconcile flip can happen), /terms and /privacy (public docs), and the
 * activation route itself. Owners and comp users default to
 * activation_status="active" so the gate never fires for them.
 */
const BYPASS_PATHS = ["/billing", "/terms", "/privacy", "/activate", "/logout", "/register", "/login"];

export default function ActivationGate({ children }) {
  const { user, logout } = useAuth();
  const billing = useBilling();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const isBypass = BYPASS_PATHS.some((p) => location.pathname.startsWith(p));

  // The user object is fetched by AuthContext on mount. On checkout
  // success we call billing.reload() which re-fetches /billing/status,
  // but the activation_status lives on the User row — we need to
  // re-fetch /me. Simplest: expose loadMe if we have it. Otherwise the
  // window reload on Stripe return handles it.
  const needsCard = !!user && user.activation_status === "pending_card"
    && !billing.is_owner && !billing.is_comp;

  const startActivation = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/activation-checkout", {
        origin_url: window.location.origin,
      });
      if (!data.checkout_url) {
        // Server told us activation isn't needed — just reload to pick up
        // the fresh activation_status.
        window.location.reload();
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  }, []);

  // Auto-kick users to Stripe on first render — they should never see a
  // usable app before adding a card. But we also let them opt into the
  // manual button below (in case the popup got blocked or the redirect
  // was slow). We only auto-redirect ONCE per session to avoid loops.
  useEffect(() => {
    if (!needsCard || isBypass) return;
    if (sessionStorage.getItem("activation_redirect_started") === "1") return;
    sessionStorage.setItem("activation_redirect_started", "1");
    startActivation();
  }, [needsCard, isBypass, startActivation]);

  if (isBypass) return children;
  if (!needsCard) return children;

  return (
    <div className="fixed inset-0 z-[9990] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center overflow-y-auto p-4" data-testid="activation-gate">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-flag-red/10 text-flag-red">
            <CreditCard className="h-6 w-6" />
          </span>
          <div>
            <h2 className="font-display text-2xl font-black text-flag-navy" data-testid="activation-gate-title">
              One last step — add a payment method
            </h2>
            <p className="text-sm text-slate-600 mt-0.5">
              We hold a card to start your 7-day free trial. You won't be charged for 7 days, and you can cancel anytime from Billing before the trial ends.
            </p>
          </div>
        </div>

        <ul className="mt-5 space-y-2 text-sm text-slate-700">
          <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> Card is captured by Stripe; Electable never sees the number.</li>
          <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> Free for 7 days on Standard ($34.99/mo). Change plan anytime from Billing.</li>
          <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> Cancel from the Billing page and keep access through your paid period.</li>
        </ul>

        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Button
            onClick={startActivation}
            disabled={busy}
            data-testid="activation-gate-continue"
            className="flex-1 h-12 bg-flag-red hover:bg-flag-red-hover text-white text-base font-black"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Continue to Stripe <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
          <Button
            variant="ghost"
            onClick={async () => { sessionStorage.removeItem("activation_redirect_started"); await logout(); navigate("/login"); }}
            className="text-slate-500 hover:text-flag-red font-semibold"
            data-testid="activation-gate-logout"
          >
            <LogOut className="h-4 w-4 mr-1.5" /> Log out
          </Button>
        </div>
      </div>
    </div>
  );
}
